import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import events from "../../data/events";

const Chatbot = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [messages, setMessages] = useState([
    {
      type: "bot",
      content:
        "Hi! I can help you find information about our events and updates. What would you like to know?",
      options: [
        "Upcoming Events",
        "Latest Updates",
        "Competition Schedule",
        "Registration Status",
      ],
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getUpcomingEvents = () => {
    const today = new Date();
    return events.filter((event) => new Date(event.date) >= today).slice(0, 3);
  };

  const handleSendMessage = async (message) => {
    // Add user message
    setMessages((prev) => [...prev, { type: "user", content: message }]);

    // Simulate bot response
    setTimeout(() => {
      let botResponse;
      switch (message.toLowerCase()) {
        case "upcoming events":
          const upcomingEvents = getUpcomingEvents();
          botResponse = {
            type: "bot",
            content: "Here are our upcoming events:",
            events: upcomingEvents,
            options: [
              "View All Events",
              "Competition Schedule",
              "Register for Event",
              "Back to Menu",
            ],
          };
          break;

        case "latest updates":
          botResponse = {
            type: "bot",
            content: "Here are our most recent updates:",
            updates: [
              {
                title: "New Competition Schedule Released",
                date: "2024-03-15",
              },
              { title: "Summer Camp Registration Open", date: "2024-03-10" },
            ],
            options: ["View All Updates", "Competition Info", "Back to Menu"],
          };
          break;

        case "competition schedule":
          botResponse = {
            type: "bot",
            content: "Our upcoming competitions:",
            competitions: events
              .filter((event) => event.category === "Competition")
              .slice(0, 3),
            options: [
              "Register for Competition",
              "View All Events",
              "Back to Menu",
            ],
          };
          break;

        case "registration status":
          botResponse = {
            type: "bot",
            content: "Which registration would you like to check?",
            options: [
              "Summer Camp",
              "Competition Teams",
              "Regular Classes",
              "Back to Menu",
            ],
          };
          break;

        case "view all events":
          navigate("/events");
          botResponse = {
            type: "bot",
            content:
              "I've opened the events page for you. Is there anything else you'd like to know?",
            options: ["Latest Updates", "Competition Schedule", "Back to Menu"],
          };
          break;

        case "view all updates":
          navigate("/updates");
          botResponse = {
            type: "bot",
            content:
              "I've opened the updates page for you. Can I help you with anything else?",
            options: [
              "Upcoming Events",
              "Competition Schedule",
              "Back to Menu",
            ],
          };
          break;

        case "back to menu":
          botResponse = {
            type: "bot",
            content: "What would you like to know?",
            options: [
              "Upcoming Events",
              "Latest Updates",
              "Competition Schedule",
              "Registration Status",
            ],
          };
          break;

        default:
          botResponse = {
            type: "bot",
            content:
              "I'm not sure I understand. Can you try one of these options?",
            options: [
              "Upcoming Events",
              "Latest Updates",
              "Competition Schedule",
              "Registration Status",
            ],
          };
      }

      setMessages((prev) => [...prev, botResponse]);
    }, 500);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      handleSendMessage(inputValue);
      setInputValue("");
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat button - always visible */}
      <button
        onClick={() => {
          if (!isOpen) {
            setIsOpen(true);
            setIsMinimized(false);
          } else if (isMinimized) {
            setIsMinimized(false);
          } else {
            setIsOpen(false);
          }
        }}
        className="p-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-colors"
        aria-label={isOpen ? "Close chat" : "Open chat"}
        title="Chat Assistant"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
      </button>

      {/* Chat window - only visible when open */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 ease-in-out">
          {/* Chat header with controls */}
          <div className="bg-blue-600 text-white p-3 flex justify-between items-center">
            <h3 className="font-medium">Chat Assistant</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1 hover:bg-blue-700 rounded"
                title={isMinimized ? "Expand" : "Minimize"}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMinimized ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 15l7-7 7 7"
                    />
                  )}
                </svg>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-blue-700 rounded"
                title="Close"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Chat content - conditionally rendered based on minimized state */}
          <div className={`flex flex-col ${isMinimized ? 'h-0' : 'h-96'} transition-all duration-300 ease-in-out overflow-hidden`}>
            {/* Messages area */}
            <div className="flex-1 p-4 overflow-y-auto">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex mb-4 ${message.type === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${message.type === "user" ? "bg-blue-600 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"}`}
                  >
                    <p>{message.content}</p>

                    {/* Event List */}
                    {message.events && (
                      <div className="mt-2 space-y-2">
                        {message.events.map((event, i) => (
                          <div
                            key={i}
                            className="p-2 bg-white dark:bg-gray-600 rounded-lg"
                          >
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {event.title}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {new Date(event.date).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Updates List */}
                    {message.updates && (
                      <div className="mt-2 space-y-2">
                        {message.updates.map((update, i) => (
                          <div
                            key={i}
                            className="p-2 bg-white dark:bg-gray-600 rounded-lg"
                          >
                            <p className="font-semibold text-gray-900 dark:text-gray-100">
                              {update.title}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {new Date(update.date).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Quick Reply Options */}
                    {message.options && (
                      <div className="mt-2 space-y-2">
                        {message.options.map((option) => (
                          <button
                            key={option}
                            onClick={() => handleSendMessage(option)}
                            className="block w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 rounded hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="p-3 border-t border-gray-200 dark:border-gray-700">
              <form onSubmit={handleSubmit} className="flex space-x-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                >
                  Send
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
